<script setup>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { reactive, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  selectedRecord: {
    type: Object,
    default: () => ({}),
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['close']);
const store = useStore();
const { t } = useI18n();

const formData = reactive({
  name: '',
  reason_type: 'neutral',
});

const isSubmitting = ref(false);

const rules = {
  name: { required },
};

const v$ = useVuelidate(rules, formData);

watch(
  () => props.selectedRecord,
  newRecord => {
    if (props.isEdit && newRecord) {
      formData.name = newRecord.name;
      formData.reason_type = newRecord.reason_type || 'neutral';
    }
  },
  { immediate: true, deep: true }
);

const submitForm = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  isSubmitting.value = true;
  try {
    const action = props.isEdit
      ? 'closureReasons/update'
      : 'closureReasons/create';
    const payload = props.isEdit
      ? { id: props.selectedRecord.id, ...formData }
      : formData;
    await store.dispatch(action, payload);
    useAlert(
      props.isEdit
        ? t('CLOSURE_REASONS.FORM.API.SUCCESS_UPDATE')
        : t('CLOSURE_REASONS.FORM.API.SUCCESS_CREATE')
    );
    emit('close');
  } catch (error) {
    useAlert(error?.message || t('CLOSURE_REASONS.FORM.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="h-auto overflow-auto flex flex-col pt-8">
    <woot-modal-header
      :header-title="
        isEdit
          ? $t('CLOSURE_REASONS.FORM.EDIT_TITLE')
          : $t('CLOSURE_REASONS.FORM.ADD_TITLE')
      "
    />
    <form class="flex flex-col w-full" @submit.prevent="submitForm">
      <div class="w-full px-8 pb-8">
        <label>
          {{ $t('CLOSURE_REASONS.FORM.NAME.LABEL') }}
          <input
            v-model.trim="formData.name"
            type="text"
            :placeholder="$t('CLOSURE_REASONS.FORM.NAME.PLACEHOLDER')"
            @input="v$.name.$touch"
          />
          <span v-if="v$.name.$error" class="message text-n-ruby-11">
            {{ $t('CLOSURE_REASONS.FORM.NAME.ERROR') }}
          </span>
        </label>

        <label class="mt-4">
          {{ $t('CLOSURE_REASONS.FORM.TYPE.LABEL') }}
          <select v-model="formData.reason_type">
            <option value="neutral">
              {{ $t('CLOSURE_REASONS.TYPES.NEUTRAL') }}
            </option>
            <option value="won">
              {{ $t('CLOSURE_REASONS.TYPES.WON') }}
            </option>
            <option value="lost">
              {{ $t('CLOSURE_REASONS.TYPES.LOST') }}
            </option>
          </select>
        </label>

        <div class="flex justify-end gap-2 mt-8">
          <Button variant="ghost" type="button" @click.prevent="emit('close')">
            {{ $t('CLOSURE_REASONS.FORM.CANCEL') }}
          </Button>
          <Button :is-loading="isSubmitting" type="submit">
            {{
              isEdit
                ? $t('CLOSURE_REASONS.FORM.EDIT_SUBMIT')
                : $t('CLOSURE_REASONS.FORM.ADD_SUBMIT')
            }}
          </Button>
        </div>
      </div>
    </form>
  </div>
</template>
